/**
 * KeyboardShortcutsModal — Cmd+? / Strg+? oeffnet eine Liste aller
 * globalen Tastenkuerzel (UX-Audit Phase 3.5).
 *
 * Wird einmal im Layout gemountet. Reagiert global auf
 * `keydown`-Events und kann ueberall im Produkt geoeffnet werden,
 * ohne dass eine Komponente einen eigenen Listener registrieren muss.
 *
 * Die Liste der Shortcuts ist als Daten-Array gepflegt, damit
 * neue Bindings einfach hinzukommen koennen, ohne Layout-JSX
 * anzufassen.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ASSISTANT_NAME } from '@/config/assistant';

interface Shortcut {
  /** Tasten als Array — werden in der UI mit `+` verknuepft. */
  keys: string[];
  /** Was passiert beim Druecken? */
  description: string;
}

interface ShortcutGroup {
  heading: string;
  items: Shortcut[];
}

const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
const META = isMac ? '⌘' : 'Strg';

const SHORTCUTS: ShortcutGroup[] = [
  {
    heading: 'Allgemein',
    items: [
      { keys: [META, 'K'], description: 'Suche & Befehle (CommandPalette) oeffnen' },
      { keys: [META, 'J'], description: `${ASSISTANT_NAME}-Assistent (KI-Chat) oeffnen/schliessen` },
      { keys: [META, '?'], description: 'Diese Uebersicht anzeigen' },
      { keys: ['Esc'], description: 'Dialog, Menue oder Suche schliessen' },
    ],
  },
  {
    heading: 'Navigation',
    items: [
      { keys: ['Tab'], description: 'Naechstes Element fokussieren' },
      { keys: ['Shift', 'Tab'], description: 'Vorheriges Element fokussieren' },
      { keys: ['Enter'], description: 'Fokussiertes Element aktivieren' },
    ],
  },
  {
    heading: 'Listen & Tabellen',
    items: [
      { keys: ['↑', '↓'], description: 'Zeile auswaehlen' },
      { keys: ['Enter'], description: 'Detail oeffnen' },
    ],
  },
];

function KeyChip({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 text-xs font-medium font-mono rounded-md border border-border bg-muted text-foreground shadow-sm">
      {children}
    </kbd>
  );
}

export function KeyboardShortcutsModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // `?` braucht meist Shift; auf einer deutschen Tastatur liegt es
      // auf der Shift+ß-Position. Wir akzeptieren beide Varianten —
      // mit oder ohne Shift — und matchen ueber `e.key`, das den
      // gerenderten Wert nach Shift liefert.
      if ((e.metaKey || e.ctrlKey) && e.key === '?') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Tastenkuerzel</DialogTitle>
          <DialogDescription>
            Alle globalen Shortcuts. Druecken Sie {META}+? jederzeit, um diese
            Liste wieder zu oeffnen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {SHORTCUTS.map((group) => (
            <section key={group.heading}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                {group.heading}
              </h3>
              <dl className="space-y-1.5">
                {group.items.map((s, i) => (
                  <div
                    key={`${group.heading}-${i}`}
                    className="flex items-center justify-between gap-4 py-1"
                  >
                    <dt className="text-sm text-foreground">{s.description}</dt>
                    <dd className="flex items-center gap-1 shrink-0">
                      {s.keys.map((k, ki) => (
                        <span key={ki} className="flex items-center gap-1">
                          {ki > 0 ? (
                            <span aria-hidden="true" className="text-muted-foreground text-xs">
                              +
                            </span>
                          ) : null}
                          <KeyChip>{k}</KeyChip>
                        </span>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
